import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  Users, 
  Calendar,
  Settings, 
  Activity,
  TrendingUp,
  Clock,
  FileText,
  AlertTriangle
} from 'lucide-react';
import { 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar
} from 'recharts';
import useResumenPersonalPorCliente from '../../hooks/useResumenPersonalPorCliente';
import { usePersonalList } from '../../hooks/usePersonal';
import { useEstadisticasServicios } from '../../hooks/useServicios';
import { useEstados } from '../../hooks/useEstados';
import { apiService } from '../../services/api';
import { useMinimoPersonal } from '../../hooks/useMinimoPersonal';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { PersonalInfoModal } from './PersonalInfoModal';
import { PersonalTrabajandoModal } from './PersonalTrabajandoModal';
import { PersonalAsignadoModal } from './PersonalAsignadoModal';
import { EventosModal } from './EventosModal';
import { NodosInfoModal } from './NodosInfoModal';
import { CarterasInfoModal } from './CarterasInfoModal';
import { PersonalRecienCreados } from './PersonalRecienCreados';
import { useIsMobile } from '../../hooks/useIsMobile';

// Fecha calendario local 'YYYY-MM-DD'. toISOString() la pasa a UTC y de noche en Chile
// devuelve el día siguiente, así que la semana quedaba corrida.
const fechaLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Función para obtener los próximos 15 días
const getProximosDias = () => {
  const dias = [];
  const hoy = new Date();
  for (let i = 0; i < 15; i++) {
    const fecha = new Date(hoy);
    fecha.setDate(hoy.getDate() + i);
    dias.push(fechaLocal(fecha));
  }
  return dias;
};

const DocumentosCardBody: React.FC = () => {
  // Los tres totales los suma el servidor (/documentos/contadores). Antes se descargaba el
  // listado completo de documentos — 2,8 MB — solo para contar tres números, y lo hacía
  // cualquier cuenta que abriera el dashboard.
  const contadores = useQuery({
    queryKey: ['documentos', 'contadores', 30],
    queryFn: () => apiService.getDocumentosContadores(30),
    staleTime: 2 * 60 * 1000,
  });

  const stats = contadores.data?.data;
  const isLoading = contadores.isLoading;
  // Si la consulta falla (backend sin el endpoint todavía, o red caída) se muestra un guion:
  // un 0 se leería como "no hay documentos vencidos", que es justo lo contrario de no saber.
  const fmt = (n?: number) => (contadores.isError || n === undefined ? '—' : n.toLocaleString('es-CL'));
  const total = fmt(stats?.total);
  const vencidos = fmt(stats?.vencidos);
  const porVencer = fmt(stats?.por_vencer);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <div className="p-6 rounded-xl border border-gray-200 bg-white flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-gray-600 mb-1">Total</div>
            <div className="text-2xl font-bold text-gray-900">{total}</div>
          </div>
          <div className="p-3 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 shadow-lg">
            <FileText className="h-6 w-6 text-slate-700" />
          </div>
        </div>

        <div className="p-6 rounded-xl border border-gray-200 bg-white flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-red-600 mb-1">Vencidos</div>
            <div className="text-2xl font-bold text-red-700">{vencidos}</div>
          </div>
          <div className="p-3 rounded-xl bg-gradient-to-br from-red-100 to-red-200 shadow-lg">
            <AlertTriangle className="h-6 w-6 text-red-700" />
          </div>
        </div>

        <div className="p-6 rounded-xl border border-gray-200 bg-white flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-yellow-700 mb-1">Por vencer (≤30d)</div>
            <div className="text-2xl font-bold text-yellow-800">{porVencer}</div>
          </div>
          <div className="p-3 rounded-xl bg-gradient-to-br from-yellow-50 to-yellow-100 shadow-lg">
            <Clock className="h-6 w-6 text-yellow-700" />
          </div>
        </div>
      </div>
    </div>
  );
};

// const proximosDias = getProximosDias(); // TODO: Usar cuando se implementen eventos reales


const StatCard: React.FC<{
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  href?: string;
  onClick?: () => void;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  children?: React.ReactNode;
}> = ({ title, value, icon, color, href, onClick, trend, children }) => {
  const content = (
    <div className={`p-3 sm:p-6 rounded-xl border border-gray-200 bg-white hover:shadow-lg transition-all duration-300 ${(href || onClick) ? 'cursor-pointer hover:scale-105' : ''}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-xs sm:text-sm font-medium text-gray-600 mb-0.5 sm:mb-1 truncate">{title}</p>
          <p className="text-xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">{value}</p>
          {trend && (
            <div className={`flex items-center text-xs sm:text-sm ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}>
              <TrendingUp className={`h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 ${trend.isPositive ? '' : 'rotate-180'}`} />
              <span>{Math.abs(trend.value)}%</span>
            </div>
          )}
          {/* Render children (preview details) if provided */}
          {children && (
            <div className="mt-3 text-sm text-gray-700 space-y-1 hidden sm:block">
              {children}
            </div>
          )}
        </div>
        <div className={`p-2 sm:p-4 rounded-xl ${color} shadow-lg flex-shrink-0`}>
          {icon}
        </div>
      </div>
    </div>
  );

  if (href) {
    return <Link to={href}>{content}</Link>;
  }

  if (onClick) {
    return <div onClick={onClick}>{content}</div>;
  }

  return content;
};

export const DashboardStats: React.FC = () => {
  const isMobile = useIsMobile();
  const [showPersonalModal, setShowPersonalModal] = useState(false);
  const [showPersonalTrabajandoModal, setShowPersonalTrabajandoModal] = useState(false);
  const [showPersonalAsignadoModal, setShowPersonalAsignadoModal] = useState(false);
  const [showEventosModal, setShowEventosModal] = useState(false);
  const [showNodosModal, setShowNodosModal] = useState(false);
  const [showCarterasModal, setShowCarterasModal] = useState(false);
  const [semanaActual, setSemanaActual] = useState(true); // true = semana actual, false = próxima semana
  const [semanaActualTendencias, setSemanaActualTendencias] = useState(true); // true = semana actual, false = próxima semana
  const [semanaSeleccionada, setSemanaSeleccionada] = useState<Date>(() => getInicioSemana(new Date()));
  const [temporadaFilter, setTemporadaFilter] = useState<'base' | 'alta' | 'baja'>('base');
  const [vistaMobile, setVistaMobile] = useState<'grafico' | 'lista'>('grafico');

  // Obtener datos de personal por cliente para el gráfico (filtrados por semana seleccionada)
  const fechaInicioGrafico = useMemo(() => fechaLocal(semanaSeleccionada), [semanaSeleccionada]);
  const fechaFinGrafico = useMemo(() => {
    const d = new Date(semanaSeleccionada.getTime());
    d.setDate(d.getDate() + 6);
    return fechaLocal(d);
  }, [semanaSeleccionada]);

  const { data: personalPorClienteData } = useQuery(
    ['personal-por-cliente-grafico', fechaInicioGrafico, fechaFinGrafico],
    async () => {
      try {
        const resp = await apiService.getPersonalPorCliente({ fecha_inicio: fechaInicioGrafico, fecha_fin: fechaFinGrafico });
        return resp;
      } catch (e) {
        console.warn('No se pudo obtener personal-por-cliente por semana:', e);
        return null;
      }
    },
    { 
      staleTime: 5 * 60 * 1000
    }
  );

  // Obtener mínimos desde el endpoint oficial /api/servicios/minimo-personal
  const { data: minimoData } = useMinimoPersonal({ limit: 1000 });

  // Totales del personal en UNA consulta agregada. Antes el dashboard descargaba la lista
  // completa (3 páginas, 302 KB con rut, correo, teléfono y dirección de cada persona) solo
  // para contar cuántos hay por estado.
  const { data: contadoresPersonal, isLoading: personalLoading } = useQuery({
    queryKey: ['personal', 'contadores'],
    queryFn: () => apiService.getPersonalContadores(),
    staleTime: 2 * 60 * 1000,
  });

  // La lista completa solo se pide al abrir el modal que muestra a las personas una por una.
  const { data: personalData } = usePersonalList(1, 1000, '', {}, { enabled: showPersonalTrabajandoModal });
  const { data: estadisticasServicios, isLoading: serviciosLoading } = useEstadisticasServicios();
  
  // Obtener datos de estados
  // Request more states to avoid truncated sample lists in the dashboard
  const { data: estadosData } = useEstados({ limit: 1000 });
  const estadosList = estadosData?.data || [];
  
  const personalList = personalData?.data?.items || [];
  const totalPersonal = contadoresPersonal?.data?.total ?? 0;
  const personalActivo = contadoresPersonal?.data?.activos ?? 0;
  const personalTrabajando = personalActivo;

  // Distribución por estado: ya viene contada desde el servidor; acá solo se le pone color.
  const distribucionEstados = (contadoresPersonal?.data?.por_estado || [])
    .map((estado: { id: number; nombre: string; cantidad: number }) => {
      const cantidad = estado.cantidad;
      const porcentaje = totalPersonal > 0 ? Math.round((cantidad / totalPersonal) * 100) : 0;
      
      // Asignar colores basados en el nombre del estado
      let color = 'gray';
      const nombreLower = estado.nombre.toLowerCase();
      if (nombreLower.includes('activo') || nombreLower.includes('disponible')) color = 'green';
      else if (nombreLower.includes('examen') || nombreLower.includes('exámenes')) color = 'yellow';
      else if (nombreLower.includes('capacitación') || nombreLower.includes('capacitacion')) color = 'purple';
      else if (nombreLower.includes('vacaciones')) color = 'blue';
      else if (nombreLower.includes('licencia')) color = 'orange';
      else if (nombreLower.includes('inactivo')) color = 'red';
      else if (nombreLower.includes('asignado')) color = 'indigo';
      else if (nombreLower.includes('desvinculado')) color = 'slate';
      
      return {
        id: estado.id,
        nombre: estado.nombre,
        cantidad,
        porcentaje,
        color
      };
    })
    .sort((a: any, b: any) => b.cantidad - a.cantidad); // Ordenar por cantidad descendente
  
  const totalEventos = 0; // TODO: Implementar carga de eventos reales desde el backend
  const eventosHoy = 0; // TODO: Implementar carga de eventos reales desde el backend

  // Usar datos reales del backend
  const serviciosData = estadisticasServicios?.data;
  const dashboardStats = {
    total_personal: totalPersonal,
    personal_activo: personalActivo,
    personal_trabajando: personalTrabajando,
    total_servicios: serviciosData?.totales?.nodos || 0,
    servicios_activos: serviciosData?.totales?.nodos || 0,
    total_eventos: totalEventos,
    eventos_hoy: eventosHoy,
    total_carteras: serviciosData?.totales?.carteras || 0,
    total_clientes: serviciosData?.totales?.clientes || 0,
  };

  // Obtener total asignado desde el resumen por cliente (solo para la semana actual)
  const inicioSemanaActual = useMemo(() => {
    const hoy = new Date();
    const inicio = getInicioSemana(hoy);
    return fechaLocal(inicio);
  }, []);

  const finSemanaActual = useMemo(() => {
    const inicio = new Date(inicioSemanaActual + 'T00:00:00');
    inicio.setDate(inicio.getDate() + 6);
    return fechaLocal(inicio);
  }, [inicioSemanaActual]);

  // Llamar al hook pasando fecha_inicio y fecha_fin para limitar al rango de la semana en curso
  const { data: resumenData, isLoading: resumenLoading } = useResumenPersonalPorCliente(undefined, inicioSemanaActual, finSemanaActual);
  const resumenRows: any[] = resumenData?.data || [];
  const totalPersonasAsignadas = resumenRows.reduce((s, r) => s + (Number(r.total_personal) || 0), 0);


  // Función para obtener el inicio de la semana (lunes)
  function getInicioSemana(fecha: Date) {
    const inicio = new Date(fecha);
    const dia = inicio.getDay();
    const diff = inicio.getDate() - dia + (dia === 0 ? -6 : 1); // Ajustar para que lunes sea 1
    inicio.setDate(diff);
    inicio.setHours(0, 0, 0, 0);
    return inicio;
  }

  // Calcular número ISO de semana a partir de una fecha
  const getISOWeekNumber = (date: Date) => {
    const tmp = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    // Set to nearest Thursday: current date + 4 - current day number
    const dayNum = (tmp.getUTCDay() + 6) % 7; // Monday=0, Sunday=6
    tmp.setUTCDate(tmp.getUTCDate() + 3 - dayNum);
    const firstThursday = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 4));
    const weekNumber = 1 + Math.round(((tmp.getTime() - firstThursday.getTime()) / 86400000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
    return weekNumber;
  };

  // Función para generar datos de eventos por día
  const generarEventosPorDia = (esSemanaActual: boolean) => {
    const hoy = new Date();
    const inicioSemana = getInicioSemana(hoy);
    
    // Si es próxima semana, agregar 7 días
    if (!esSemanaActual) {
      inicioSemana.setDate(inicioSemana.getDate() + 7);
    }

    const diasSemana = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
    const eventosPorDia = [];

    for (let i = 0; i < 7; i++) {
      const fecha = new Date(inicioSemana);
      fecha.setDate(inicioSemana.getDate() + i);
      
      // Generar datos estables basados en la fecha (no aleatorios)
      const seed = fecha.getDate() + (esSemanaActual ? 0 : 100); // Diferente seed para cada semana
      const eventos = (seed % 6) + 1; // 1-6 eventos
      const completados = Math.min(eventos, (seed % 4) + 1); // 1-4 completados, máximo eventos
      
      eventosPorDia.push({
        name: diasSemana[i],
        eventos: eventos,
        completados: completados,
        fecha: fechaLocal(fecha)
      });
    }

    return eventosPorDia;
  };

  // Datos para gráfico de eventos por día
  const eventosPorDia = generarEventosPorDia(semanaActual);

  // Función para generar datos de tendencias de servicios por día
  const generarTendenciasServicios = (esSemanaActual: boolean) => {
    // Si no hay datos de personal por cliente, retornar array vacío
    if (!personalPorClienteData?.data || !Array.isArray(personalPorClienteData.data)) {
      return [];
    }

    // Filtrar solo clientes con personal asignado y mapear todos los clientes
    const clientesConDatos = (personalPorClienteData?.data || [])
      .filter((cliente: any) => cliente.total_personal_asignado > 0) // Solo clientes con personal
      .map((cliente: any) => {
        const personalAsignado = cliente.total_personal_asignado || 0;
        // Preferir el mínimo calculado por el backend (`minimo_real`) si está disponible
        let personalMinimo = cliente.personal_minimo || 0;
        let minimoAlta = 0;
        let minimoBaja = 0;
        try {
          if (minimoData && minimoData.success && Array.isArray(minimoData.data)) {
            // Buscar todos los registros para este cliente y elegir el más reciente por created_at (fallback updated_at)
            const matches = minimoData.data.filter((m: any) => Number(m.cliente_id) === Number(cliente.cliente_id));
            if (matches.length > 0) {
              const latest = matches.reduce((best: any, cur: any) => {
                const bestTime = Date.parse(best?.created_at || best?.updated_at || '') || 0;
                const curTime = Date.parse(cur?.created_at || cur?.updated_at || '') || 0;
                return curTime >= bestTime ? cur : best;
              }, matches[0]);

              // Prefer `minimo_base` (base table column), luego `minimo_real`, luego `minimo_personal`
              const candidate = (typeof latest.minimo_base !== 'undefined' && latest.minimo_base !== null)
                ? latest.minimo_base
                : (typeof latest.minimo_real !== 'undefined' && latest.minimo_real !== null)
                  ? latest.minimo_real
                  : (typeof latest.minimo_personal !== 'undefined' && latest.minimo_personal !== null)
                    ? latest.minimo_personal
                    : undefined;

              if (typeof candidate !== 'undefined' && candidate !== null) {
                personalMinimo = Number(candidate);
              }

              // Extraer valores por temporada
              if (typeof latest.minimo_temporada_alta !== 'undefined' && latest.minimo_temporada_alta !== null) {
                minimoAlta = Number(latest.minimo_temporada_alta);
              } else {
                minimoAlta = personalMinimo;
              }
              if (typeof latest.minimo_temporada_baja !== 'undefined' && latest.minimo_temporada_baja !== null) {
                minimoBaja = Number(latest.minimo_temporada_baja);
              } else {
                minimoBaja = personalMinimo;
              }
            }
          }
        } catch (e) {
          // ignore and keep cliente.personal_minimo
        }
        let clienteNombre = cliente.cliente_nombre || `Cliente ${cliente.cliente_id}`;
        const clienteNombreCompleto = clienteNombre;
        
        // Acortar nombres largos para mejor visualización
        if (clienteNombre.length > 25) {
          // Tomar las primeras palabras significativas
          const palabras = clienteNombre.split(' - ');
          clienteNombre = palabras[0];
          
          // Si sigue siendo muy largo, tomar solo las primeras 3 palabras
          if (clienteNombre.length > 25) {
            const primerasPalabras = clienteNombre.split(' ').slice(0, 3).join(' ');
            clienteNombre = primerasPalabras + '...';
          }
        }
        
        return {
          name: clienteNombre,
          nombreCompleto: clienteNombreCompleto,
          fecha: fechaLocal(new Date()),
          personalMinimo: personalMinimo,
          minimoBase: personalMinimo,
          minimoAlta: minimoAlta,
          minimoBaja: minimoBaja,
          personalAsignado: personalAsignado,
          cliente_id: cliente.cliente_id
        };
      });

    return clientesConDatos;
  };

  // Datos para gráfico de tendencia de servicios
  const tendenciaServicios = generarTendenciasServicios(semanaActualTendencias);

  // Información de la semana mostrada en el gráfico (usa la misma bandera semanaActualTendencias)
  const semanaInfo = useMemo(() => {
    const inicio = getInicioSemana(semanaSeleccionada);
    const fin = new Date(inicio);
    fin.setDate(inicio.getDate() + 6);
    const weekNumber = getISOWeekNumber(inicio);
    return {
      weekNumber,
      start: inicio.toLocaleDateString('es-CL'),
      end: fin.toLocaleDateString('es-CL')
    };
  }, [semanaSeleccionada]);

  if (personalLoading || serviciosLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={`loading-card-${index}`} className="p-6 rounded-xl border border-gray-200 bg-white">
            <div className="flex items-center justify-center h-24">
              <LoadingSpinner size="md" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      {/* Tarjetas principales con animaciones */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 mb-6 sm:mb-8">
        <div className="stagger-item animate-delay-100">
          <StatCard
            title="Total Personal"
            value={dashboardStats.total_personal}
            icon={<Users className="h-6 w-6 text-white" />}
            color="bg-gradient-to-br from-blue-500 to-blue-600"
            onClick={() => setShowPersonalModal(true)}
          />
        </div>
        <div className="stagger-item animate-delay-200">
          <StatCard
            title="Personal Activo"
            value={dashboardStats.personal_trabajando}
            icon={<Activity className="h-6 w-6 text-white" />}
            color="bg-gradient-to-br from-green-500 to-green-600"
            onClick={() => setShowPersonalTrabajandoModal(true)}
          />
        </div>
        <div className="stagger-item animate-delay-300">
          <StatCard
            title="Personal Asignado"
            value={resumenLoading ? dashboardStats.total_personal : totalPersonasAsignadas}
            icon={<Users className="h-6 w-6 text-white" />}
            color="bg-gradient-to-br from-purple-500 to-purple-600"
            onClick={() => setShowPersonalAsignadoModal(true)}
          />
        </div>
      </div>


      {/* Gráfico de Programación de Servicios - Expandido */}
      <div className="mb-8">
        {/* Programación de servicios */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Programación de Servicios</h3>
              <div className="text-sm text-gray-500 flex flex-wrap items-center gap-2 sm:gap-3 mt-1 sm:mt-0">
                <div>Semana {semanaInfo.weekNumber} — {semanaInfo.start} → {semanaInfo.end}</div>
                <div className="flex items-center space-x-1">
                  <button onClick={() => setSemanaSeleccionada(s => { const n = new Date(s); n.setDate(n.getDate() - 7); return n; })} className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200">◀</button>
                  <button onClick={() => setSemanaSeleccionada(getInicioSemana(new Date()))} className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200">Hoy</button>
                  <button onClick={() => setSemanaSeleccionada(s => { const n = new Date(s); n.setDate(n.getDate() + 7); return n; })} className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200">▶</button>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between sm:justify-end gap-3">
              <div className="flex bg-gray-100 rounded-lg p-1 text-sm">
                <button
                  onClick={() => setTemporadaFilter('base')}
                  className={`px-2.5 sm:px-3 py-1 rounded-md font-medium transition-colors whitespace-nowrap ${
                    temporadaFilter === 'base' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Contrato
                </button>
                <button
                  onClick={() => setTemporadaFilter('baja')}
                  className={`px-2.5 sm:px-3 py-1 rounded-md font-medium transition-colors whitespace-nowrap ${
                    temporadaFilter === 'baja' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  T. Baja
                </button>
                <button
                  onClick={() => setTemporadaFilter('alta')}
                  className={`px-2.5 sm:px-3 py-1 rounded-md font-medium transition-colors whitespace-nowrap ${
                    temporadaFilter === 'alta' ? 'bg-white text-amber-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  T. Alta
                </button>
              </div>
              <TrendingUp className="h-5 w-5 text-gray-400 flex-shrink-0" />
            </div>
          </div>
          {(() => {
            const activeMinimoKey = temporadaFilter === 'base' ? 'minimoBase' : temporadaFilter === 'alta' ? 'minimoAlta' : 'minimoBaja';
            const activeMinimoLabel = temporadaFilter === 'base' ? 'Personal Contrato' : temporadaFilter === 'alta' ? 'T. Alta' : 'T. Baja';
            const activeMinimoColor = temporadaFilter === 'base' ? '#3B82F6' : temporadaFilter === 'alta' ? '#F59E0B' : '#6366F1';
            const activeDotColor = temporadaFilter === 'base' ? 'bg-blue-500' : temporadaFilter === 'alta' ? 'bg-amber-500' : 'bg-indigo-500';
            return isMobile ? (
              <>
                <div className="flex bg-gray-100 rounded-lg p-1 text-sm mb-4">
                  <button
                    onClick={() => setVistaMobile('grafico')}
                    className={`flex-1 px-3 py-1.5 rounded-md font-medium transition-colors ${
                      vistaMobile === 'grafico' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600'
                    }`}
                  >
                    Gráfico
                  </button>
                  <button
                    onClick={() => setVistaMobile('lista')}
                    className={`flex-1 px-3 py-1.5 rounded-md font-medium transition-colors ${
                      vistaMobile === 'lista' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600'
                    }`}
                  >
                    Lista por cliente
                  </button>
                </div>
                {vistaMobile === 'grafico' ? (
                  <>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={tendenciaServicios}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="name"
                          stroke="#6b7280"
                          angle={-35}
                          textAnchor="end"
                          height={90}
                          interval={0}
                          style={{ fontSize: '10px' }}
                        />
                        <YAxis stroke="#6b7280" style={{ fontSize: '11px' }} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                          }}
                          formatter={(value: any, name: any) => {
                            if (name === activeMinimoKey) return [`${value} personas`, activeMinimoLabel];
                            if (name === 'personalAsignado') return [`${value} personas`, 'Personal Asignado'];
                            return [value, name];
                          }}
                        />
                        <Bar
                          dataKey={activeMinimoKey}
                          fill={activeMinimoColor}
                          name={activeMinimoKey}
                          radius={[4, 4, 0, 0]}
                        />
                        <Bar
                          dataKey="personalAsignado"
                          fill="#10B981"
                          name="personalAsignado"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="mt-4 flex justify-center flex-wrap gap-x-6 gap-y-2">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full mr-2 ${activeDotColor}`}></div>
                        <span className="text-sm text-gray-600">{activeMinimoLabel}</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                        <span className="text-sm text-gray-600">Personal Asignado</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="space-y-2">
                    {tendenciaServicios.length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4">Sin datos para esta semana</p>
                    )}
                    {tendenciaServicios.map((row: any) => {
                      const minimoActivo = temporadaFilter === 'base' ? row.minimoBase : temporadaFilter === 'alta' ? row.minimoAlta : row.minimoBaja;
                      const cumple = row.personalAsignado >= minimoActivo;
                      return (
                        <div key={row.cliente_id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-800 truncate" title={row.nombreCompleto}>{row.name}</div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {activeMinimoLabel}: <span className="font-semibold text-gray-700">{minimoActivo}</span>
                              {' · '}Asignado: <span className="font-semibold text-green-700">{row.personalAsignado}</span>
                            </div>
                          </div>
                          {minimoActivo === 0 ? (
                            <span className="flex-shrink-0 inline-block px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500">Sin dato</span>
                          ) : cumple ? (
                            <span className="flex-shrink-0 inline-block px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 font-medium">✓ Cubre</span>
                          ) : (
                            <span className="flex-shrink-0 inline-block px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700 font-medium">✗ Bajo</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={tendenciaServicios}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="name"
                      stroke="#6b7280"
                      angle={-25}
                      textAnchor="end"
                      height={100}
                      interval={0}
                      style={{ fontSize: '12px' }}
                    />
                    <YAxis stroke="#6b7280" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }}
                      formatter={(value: any, name: any) => {
                        if (name === activeMinimoKey) return [`${value} personas`, activeMinimoLabel];
                        if (name === 'personalAsignado') return [`${value} personas`, 'Personal Asignado'];
                        return [value, name];
                      }}
                    />
                    <Bar
                      dataKey={activeMinimoKey}
                      fill={activeMinimoColor}
                      name={activeMinimoKey}
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="personalAsignado"
                      fill="#10B981"
                      name="personalAsignado"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-4 flex justify-center space-x-6">
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full mr-2 ${activeDotColor}`}></div>
                    <span className="text-sm text-gray-600">{activeMinimoLabel}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    <span className="text-sm text-gray-600">Personal Asignado</span>
                  </div>
                </div>
                {/* Tabla de contrato por cliente */}
                {tendenciaServicios.length > 0 && (
                  <div className="mt-6 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
                          <th className="pb-2 pr-4 font-medium">Cliente</th>
                          <th className="pb-2 px-3 font-medium text-center">Contrato</th>
                          <th className="pb-2 px-3 font-medium text-center">T. Baja</th>
                          <th className="pb-2 px-3 font-medium text-center">T. Alta</th>
                          <th className="pb-2 px-3 font-medium text-center">Asignado</th>
                          <th className="pb-2 pl-3 font-medium text-center">Estado</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {tendenciaServicios.map((row: any) => {
                          const minimoActivo = temporadaFilter === 'base' ? row.minimoBase : temporadaFilter === 'alta' ? row.minimoAlta : row.minimoBaja;
                          const cumple = row.personalAsignado >= minimoActivo;
                          return (
                            <tr key={row.cliente_id} className="hover:bg-gray-50">
                              <td className="py-2 pr-4 text-gray-800 font-medium" title={row.nombreCompleto}>{row.name}</td>
                              <td className="py-2 px-3 text-center text-blue-700 font-semibold">{row.minimoBase}</td>
                              <td className={`py-2 px-3 text-center font-semibold ${temporadaFilter === 'baja' ? 'text-indigo-700 bg-indigo-50 rounded' : 'text-gray-500'}`}>{row.minimoBaja}</td>
                              <td className={`py-2 px-3 text-center font-semibold ${temporadaFilter === 'alta' ? 'text-amber-700 bg-amber-50 rounded' : 'text-gray-500'}`}>{row.minimoAlta}</td>
                              <td className="py-2 px-3 text-center text-green-700 font-semibold">{row.personalAsignado}</td>
                              <td className="py-2 pl-3 text-center">
                                {minimoActivo === 0 ? (
                                  <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500">Sin dato</span>
                                ) : cumple ? (
                                  <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 font-medium">✓ Cubre</span>
                                ) : (
                                  <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700 font-medium">✗ Bajo</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* Eventos por día - OCULTO (comentado para uso futuro) */}
        {false && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Eventos por Día</h3>
            <div className="flex items-center space-x-2">
              <Clock className="h-5 w-5 text-gray-400" />
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setSemanaActual(true)}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                    semanaActual
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Semana Actual
                </button>
                <button
                  onClick={() => setSemanaActual(false)}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                    !semanaActual
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Próxima Semana
                </button>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={eventosPorDia}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
                labelFormatter={(value, payload) => {
                  if (payload && payload[0]) {
                    const data = payload[0].payload;
                    return `${data.name} - ${new Date(data.fecha).toLocaleDateString('es-CL')}`;
                  }
                  return value;
                }}
              />
              <Line 
                type="monotone" 
                dataKey="eventos" 
                stroke="#8B5CF6" 
                strokeWidth={3}
                dot={{ fill: '#8B5CF6', strokeWidth: 2, r: 6 }}
                name="Eventos Programados"
              />
              <Line 
                type="monotone" 
                dataKey="completados" 
                stroke="#10B981" 
                strokeWidth={3}
                dot={{ fill: '#10B981', strokeWidth: 2, r: 6 }}
                name="Eventos Completados"
              />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-4 flex justify-center space-x-6">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
              <span className="text-sm text-gray-600">Eventos Programados</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
              <span className="text-sm text-gray-600">Eventos Completados</span>
            </div>
          </div>
          </div>
        )}

        {/* Documentos - resumen rápido */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm mt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Documentación</h3>
            </div>
            <div className="flex items-center space-x-2">
              <Link to="/personal/estado-documentacion" className="text-sm text-blue-600 hover:underline">Ver todo</Link>
            </div>
          </div>
          <DocumentosCardBody />
        </div>
      </div>

      {/* Nuevo personal (etiqueta "Nuevo") */}
      <div className="mb-8">
        <PersonalRecienCreados />
      </div>

      {/* Tarjetas secundarias */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="stagger-item animate-delay-500">
          <StatCard
            title="Total Carteras"
            value={dashboardStats.total_carteras}
            icon={<Users className="h-6 w-6 text-white" />}
            color="bg-gradient-to-br from-purple-500 to-purple-600"
            href="/servicios"
          />
        </div>
        <div className="stagger-item animate-delay-600">
          <StatCard
            title="Total Clientes"
            value={dashboardStats.total_clientes}
            icon={<Users className="h-6 w-6 text-white" />}
            color="bg-gradient-to-br from-indigo-500 to-indigo-600"
            href="/servicios"
          />
        </div>
        <div className="stagger-item animate-delay-700">
          <StatCard
            title="Total Nodos"
            value={dashboardStats.total_servicios}
            icon={<Settings className="h-6 w-6 text-white" />}
            color="bg-gradient-to-br from-orange-500 to-orange-600"
            href="/servicios"
          />
        </div>
      </div>


      {/* Modal de información del personal */}
      <PersonalInfoModal
        isOpen={showPersonalModal}
        onClose={() => setShowPersonalModal(false)}
        totalPersonal={dashboardStats.total_personal}
        distribucionEstados={distribucionEstados}
      />

      {/* Modal de personal activo */}
      <PersonalTrabajandoModal
        isOpen={showPersonalTrabajandoModal}
        onClose={() => setShowPersonalTrabajandoModal(false)}
        personalTrabajando={(() => {
          const estadoActivo = estadosList.find((e: any) => e.nombre && e.nombre.toLowerCase() === 'activo');

          if (!estadoActivo) {
            console.warn('⚠️ No se encontró el estado "Activo"');
            return [] as any;
          }

          const personalFiltrado = personalList.filter(p => p.estado_id === estadoActivo.id);

          return personalFiltrado.map(p => {
            const ubicacionObj = (p as any).ubicacion;
            const comunaFromUbicacion = ubicacionObj && typeof ubicacionObj === 'object' ? (ubicacionObj.comuna || ubicacionObj.ciudad || ubicacionObj.region) : undefined;
            const comuna = (typeof ubicacionObj === 'string' && ubicacionObj) || comunaFromUbicacion || (p as any).comuna || p.zona_geografica || 'No especificada';

            const nombrePreferido = p.nombres || ((p.nombre || '') + (p.apellido ? ' ' + p.apellido : '')).trim();
            const rut = p.rut || (p as any).rut_persona || '';

            return {
              id: p.id,
              nombre: nombrePreferido,
              apellido: p.apellido || '',
              nombres: p.nombres,
              rut,
              cargo: p.cargo,
              ubicacion: String(comuna),
              servicioAsignado: {
                id: p.servicio_id || '1',
                nombre: 'Servicio Asignado',
                categoria: 'General',
                zonaGestion: String(comuna)
              },
              estadoActividad: {
                label: 'Trabajando'
              }
            } as any;
          });
        })()}
      />

      {/* Modal de personal asignado */}
      <PersonalAsignadoModal
        isOpen={showPersonalAsignadoModal}
        onClose={() => setShowPersonalAsignadoModal(false)}
      />

      {/* Modal de eventos */}
      <EventosModal
        isOpen={showEventosModal}
        onClose={() => setShowEventosModal(false)}
        eventos={[]}
      />

      {/* Modal de información de nodos */}
      <NodosInfoModal
        isOpen={showNodosModal}
        onClose={() => setShowNodosModal(false)}
        totalNodos={dashboardStats.total_servicios}
      />

      {/* Modal de información de carteras */}
      <CarterasInfoModal
        isOpen={showCarterasModal}
        onClose={() => setShowCarterasModal(false)}
        totalCarteras={dashboardStats.total_carteras}
      />
    </>
  );
};